/**
 * The lots a receipt became, and the dialog that makes one from a line.
 *
 * PHASE 3 OF THE PURCHASE ORDER BUILD-OUT (Ed, 2026-10-30: "the lot created from a receipt"). A
 * released receipt's Lines received table says what arrived against each order line; this pane shows,
 * per line, how much of that is in lots already, and "Create lot" opens a dialog that asks for the
 * line, the quantity (the line's unlotted remainder by default), the lot number as marked, the dates,
 * the location and whether to hold it for inspection. The server (/t1/v1/receipt_lots POST) opens the
 * lot through its own door with its first transfer written in, draws its edges, and writes back onto
 * the receipt's line which lot it became; a refusal (CC-LOTR-*) is shown as the server phrased it.
 *
 * THE LOTS LISTED ARE READ UNDER THE READER'S OWN SIGHT, as an inventory's are: an unseen lot is its
 * ID, grey, with no link.
 */
import React, {useCallback, useContext, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, TextField,
        Typography} from "@mui/material";
import {AppContext} from "../../util/AppContext";
import {makeCresAxios} from "../../util/Axios";
import {Blue3, Grey2, Grey6, Grey7, Grey8, Grey17} from "../../theme/styles";

type Lot = {ObjectUSID: string; RevisionUSID?: string; ObjectID?: string | null; Name?: string | null; Status?: string | null;
            Visible: boolean; LotStatus?: string | null; QuantityOnHand?: any; QuantityUnit?: string | null; Location?: string | null;
            LineId?: string | null};
type Line = {LineId?: string | null; Item?: string | null; SupplierPartNumber?: string | null; Description?: string | null;
             Unit?: string | null; QuantityReceived?: any; QuantityLotted?: any; Lots?: string | null};

function listOf<T>(held: T[] | T | undefined | null): T[] {
    if (held === null || held === undefined) return [];
    return Array.isArray(held) ? held : [held];
}

const left = (line: Line) => Number(line.QuantityReceived ?? 0) - Number(line.QuantityLotted ?? 0);

export default function ReceiptLotsPane({objectUSID, status, canCreate}: {
    /** The receipt's LOGICAL object. */
    objectUSID: string;
    status?: string | null;
    /** Whether the reader may create lots; the server checks regardless. */
    canCreate?: boolean;
}) {
    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);
    const navigate = useNavigate();
    const [lots, setLots] = useState<Lot[] | null>(null);
    const [lines, setLines] = useState<Line[]>([]);
    const [hidden, setHidden] = useState(0);
    const [problem, setProblem] = useState<string | null>(null);
    const [creating, setCreating] = useState<Line | null>(null);
    const released = String(status ?? "").toUpperCase() === "RELEASED";

    const load = useCallback(() => {
        if (!objectUSID) return;
        api.get("t1/v1/receipt_lots", {params: {ReceivableObjectUSID: objectUSID}})
            .then((response: any) => {
                const message = response?.data?.PlatformMessage ?? {};
                setLots(listOf<Lot>(message?.ListOfReceiptLot?.ReceiptLot));
                setLines(listOf<Line>(message?.ListOfReceivedLine?.ReceivedLine));
                setHidden(message.HiddenCount ?? 0);
                setProblem(null);
            })
            .catch((failure: any) => {
                setLots([]);
                setProblem(failure?.response?.data?.Cause ?? "The lots could not be read.");
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectUSID]);

    useEffect(() => { load(); }, [load]);

    const describe = (line: Line) => line.Description || line.SupplierPartNumber || line.LineId || "line";

    return (
        <Box sx={{border: `1px solid ${Grey2}`, borderRadius: "6px", padding: "12px 14px"}}>
            <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8, marginBottom: "8px"}}>
                Lots from this receipt{lots ? ` (${lots.length})` : ""}
            </Typography>
            {problem && <Typography sx={{fontSize: 13, color: "#B3261E", marginBottom: "8px"}}>{problem}</Typography>}
            {lots === null && <Typography sx={{fontSize: 13, color: Grey7}}>Reading…</Typography>}

            {/* THE LINES, with what is still to be put in lots, and the one action per line. */}
            {lines.length > 0 && (
                <Box sx={{display: "grid", gridTemplateColumns: "0.9fr 2.4fr 1fr 1fr 1fr auto", gap: "6px 12px", alignItems: "center",
                          marginBottom: "14px"}}>
                    {["Line", "What", "Received", "In lots", "Left", ""].map((head, index) => (
                        <Typography key={`${head}${index}`} sx={{fontSize: 11.5, color: Grey7, textTransform: "uppercase", letterSpacing: "0.04em"}}>
                            {head}
                        </Typography>
                    ))}
                    {lines.map(line => (
                        <React.Fragment key={String(line.LineId)}>
                            <Typography sx={{fontSize: 13, color: Grey17}}>{line.LineId ?? "—"}</Typography>
                            <Typography sx={{fontSize: 13, color: Grey17}}>{describe(line)}</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{line.QuantityReceived ?? 0}{line.Unit ? ` ${line.Unit}` : ""}</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{line.QuantityLotted ?? 0}</Typography>
                            <Typography sx={{fontSize: 12.5, color: left(line) > 0 ? Grey17 : Grey6}}>{left(line)}</Typography>
                            {canCreate !== false && released && left(line) > 0 ? (
                                <Button size="small" variant="outlined" sx={{textTransform: "none", fontSize: 12}}
                                        onClick={() => setCreating(line)}>Create lot</Button>
                            ) : <span/>}
                        </React.Fragment>
                    ))}
                </Box>
            )}
            {!released && lines.length > 0 && (
                <Typography sx={{fontSize: 12.5, color: Grey7, marginBottom: "10px"}}>
                    Lots are made from a released receipt. Release this one first.
                </Typography>
            )}

            {lots && lots.length === 0 && (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6}}>No lots have been made from this receipt yet.</Typography>
            )}
            {lots && lots.length > 0 && (
                <Box sx={{display: "grid", gridTemplateColumns: "1.1fr 2.4fr 0.9fr 1fr 1fr 1.2fr", gap: "6px 12px", alignItems: "center"}}>
                    {["ID", "Name", "Line", "Lot status", "On hand", "Location"].map((head, index) => (
                        <Typography key={`${head}${index}`} sx={{fontSize: 11.5, color: Grey7, textTransform: "uppercase", letterSpacing: "0.04em"}}>
                            {head}
                        </Typography>
                    ))}
                    {lots.map(lot => lot.Visible ? (
                        <React.Fragment key={lot.ObjectUSID}>
                            <Typography sx={{fontSize: 13, color: Blue3, cursor: "pointer"}}
                                        onClick={() => lot.RevisionUSID && navigate(`/object/${lot.RevisionUSID}`)}>{lot.ObjectID ?? "—"}</Typography>
                            <Typography sx={{fontSize: 13, color: Grey17}}>{lot.Name ?? "—"}</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{lot.LineId ?? "—"}</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{lot.LotStatus ?? "—"}</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{lot.QuantityOnHand ?? "—"}{lot.QuantityUnit ? ` ${lot.QuantityUnit}` : ""}</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{lot.Location ?? "—"}</Typography>
                        </React.Fragment>
                    ) : (
                        <React.Fragment key={lot.ObjectUSID}>
                            <Typography sx={{fontSize: 13, color: Grey6}} title="This lot is not visible to you.">{lot.ObjectID ?? "—"}</Typography>
                            <Typography sx={{fontSize: 13, color: Grey6, fontStyle: "italic", gridColumn: "span 5"}}>Not visible to you</Typography>
                        </React.Fragment>
                    ))}
                </Box>
            )}
            {hidden > 0 && (
                <Typography sx={{fontSize: 12.5, color: Grey7, marginTop: "8px"}}>
                    {hidden} {hidden === 1 ? "lot is" : "lots are"} not visible to you.
                </Typography>
            )}
            {creating && (
                <CreateLotDialog api={api} objectUSID={objectUSID} line={creating} lines={lines}
                                 onClose={() => setCreating(null)} onCreated={() => { setCreating(null); load(); }}/>
            )}
        </Box>
    );
}

function CreateLotDialog({api, objectUSID, line, lines, onClose, onCreated}: {
    api: any; objectUSID: string; line: Line; lines: Line[]; onClose: () => void; onCreated: () => void;
}) {
    const [lineId, setLineId] = useState(String(line.LineId ?? ""));
    const chosen = lines.find(one => String(one.LineId) === lineId) ?? line;
    const [quantity, setQuantity] = useState(String(left(chosen)));
    const [lotNumber, setLotNumber] = useState("");
    const [expiry, setExpiry] = useState("");
    const [made, setMade] = useState("");
    const [location, setLocation] = useState("Receiving");
    const [quarantine, setQuarantine] = useState<boolean | null>(null);
    const [problem, setProblem] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const create = () => {
        setBusy(true);
        api.post("t1/v1/receipt_lots", {
            ReceivableObjectUSID: objectUSID, LineId: lineId, Quantity: quantity || null, LotNumber: lotNumber || null,
            ExpiryDate: expiry || null, ManufactureDate: made || null, Location: location || null,
            Quarantine: quarantine === null ? null : quarantine,
        }).then(() => onCreated())
          .catch((failure: any) => { setProblem(failure?.response?.data?.Cause ?? "The lot could not be created."); setBusy(false); });
    };

    const field = (label: string, value: string, set: (v: string) => void, extra: any = {}) => (
        <TextField size="small" label={label} value={value} onChange={e => set(e.target.value)} fullWidth {...extra}/>
    );

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{fontSize: 16}}>Create a lot from this receipt</DialogTitle>
            <DialogContent sx={{display: "flex", flexDirection: "column", gap: "14px", paddingTop: "8px !important"}}>
                <TextField select size="small" label="Order line" value={lineId} fullWidth
                           onChange={e => { setLineId(e.target.value); const next = lines.find(one => String(one.LineId) === e.target.value); if (next) setQuantity(String(left(next))); }}>
                    {lines.filter(one => left(one) > 0).map(one => (
                        <MenuItem key={String(one.LineId)} value={String(one.LineId)}>
                            {one.LineId} — {one.Description || one.SupplierPartNumber || "line"} ({left(one)} left{one.Unit ? ` ${one.Unit}` : ""})
                        </MenuItem>
                    ))}
                </TextField>
                {field("Quantity into this lot", quantity, setQuantity, {type: "number", slotProps: {htmlInput: {min: 0, step: "any"}}})}
                {field("Lot number (as marked)", lotNumber, setLotNumber)}
                <Box sx={{display: "flex", gap: "12px"}}>
                    {field("Manufacture date", made, setMade, {type: "date", slotProps: {inputLabel: {shrink: true}}})}
                    {field("Expiry date", expiry, setExpiry, {type: "date", slotProps: {inputLabel: {shrink: true}}})}
                </Box>
                {field("Location", location, setLocation)}
                <FormControlLabel control={<Checkbox checked={quarantine === null ? true : quarantine}
                                                     indeterminate={quarantine === null}
                                                     onChange={e => setQuarantine(e.target.checked)}/>}
                                  label={quarantine === null ? "Hold for inspection as the receipt says" : quarantine ? "Hold for inspection (Quarantine)" : "Accepted, no inspection"}/>
                <Typography sx={{fontSize: 12.5, color: Grey7}}>
                    The lot opens with a Receipt transfer into the location, and a Move into Quarantine when held, as its first lines.
                </Typography>
                {problem && <Typography sx={{fontSize: 13, color: "#B3261E"}}>{problem}</Typography>}
            </DialogContent>
            <DialogActions sx={{padding: "12px 24px"}}>
                <Button onClick={onClose} sx={{textTransform: "none"}}>Cancel</Button>
                <Button variant="contained" disableElevation onClick={create} disabled={busy || !lineId} sx={{textTransform: "none"}}>
                    {busy ? "Creating…" : "Create lot"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
